import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useETSFDaily, calcDailyResult, type ChecklistResponse } from '../../../hooks/useETSFDaily'
import { useAuth } from '../../../contexts/AuthContext'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

const DAILY_ITEMS = [
  { section: 'Delivery Note & Collection Note', item: 'Driver Print Name verified (Driving License or Company ID checked)' },
  { section: 'Delivery Note & Collection Note', item: 'Date and Time recorded on note' },
  { section: 'Delivery Note & Collection Note', item: 'Driver Signature obtained' },
  { section: 'Warehouse Vehicle In & Out Management', item: 'Vehicle in/out book updated (current format)' },
  { section: 'Warehouse Vehicle In & Out Management', item: 'Every vehicle entry and exit time recorded' },
  { section: 'Release Notes & Vehicle Logs', item: 'Every Release Note: signed, time stamped and completed correctly' },
  { section: 'Release Notes & Vehicle Logs', item: 'Airfreight Vehicle Log: all fields filled, complete, and page marked' },
  { section: 'Release Notes & Vehicle Logs', item: 'Seafreight Vehicle Log: all fields filled, complete, and page marked' },
  { section: 'Release Notes & Vehicle Logs', item: 'Road Movements Vehicle Log: all fields filled, complete, and page marked' },
]

interface ChecklistItemState extends ChecklistResponse {
  response: 'yes' | 'no' | 'na'
}

export default function DailyChecklistNew() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { userEmail } = useAuth()
  const { createChecklist } = useETSFDaily()

  const queryLocation = searchParams.get('loc') || 'BHX'
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    location: queryLocation,
    checklist_date: new Date().toISOString().split('T')[0],
    completed_by: '',
    job_ref: '',
  })

  const [responses, setResponses] = useState<ChecklistItemState[]>(
    DAILY_ITEMS.map((item, idx) => ({
      id: `temp-${idx}`,
      checklist_id: '',
      section: item.section,
      item_description: item.item,
      response: 'na' as const,
      notes: null,
    }))
  )

  const handleResponseChange = (index: number, value: 'yes' | 'no' | 'na') => {
    const updated = [...responses]
    updated[index] = { ...updated[index], response: value }
    setResponses(updated)
  }

  const handleNotesChange = (index: number, value: string) => {
    const updated = [...responses]
    updated[index] = { ...updated[index], notes: value || null }
    setResponses(updated)
  }

  const calculateResult = (): string => {
    const result = calcDailyResult(responses.map(r => ({ response: r.response })))
    return result
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      if (!formData.completed_by.trim()) {
        throw new Error('Completed by is required')
      }
      if (!formData.job_ref.trim()) {
        throw new Error('Job Ref is required')
      }

      const checklistData = {
        location: formData.location,
        checklist_date: formData.checklist_date,
        completed_by: formData.completed_by,
        job_ref: formData.job_ref.trim(),
        overall_result: calculateResult(),
        notes: null,
        created_by: userEmail,
      }

      const responsesToCreate = responses.map(r => ({
        section: r.section,
        item_description: r.item_description,
        response: r.response,
        notes: r.notes,
      }))

      await createChecklist(checklistData, responsesToCreate)
      navigate('/customs/etsf')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const currentResult = calculateResult()
  const groupedBySection = DAILY_ITEMS.reduce((acc, item, idx) => {
    const section = item.section
    if (!acc[section]) acc[section] = []
    acc[section].push(idx)
    return acc
  }, {} as Record<string, number[]>)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/customs/etsf" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
          ← Back
        </Link>

        <h1 className="text-2xl font-bold mb-6">New Mid-week Checklist</h1>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {error && <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

          {/* Header Fields */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className={LABEL_CLS}>Location</label>
              <select
                className={INPUT_CLS}
                value={formData.location}
                onChange={e => setFormData(f => ({ ...f, location: e.target.value }))}
              >
                <option value="BHX">BHX</option>
                <option value="LHR">LHR</option>
              </select>
            </div>

            <div>
              <label className={LABEL_CLS}>Date</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={formData.checklist_date}
                onChange={e => setFormData(f => ({ ...f, checklist_date: e.target.value }))}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Completed By</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.completed_by}
                onChange={e => setFormData(f => ({ ...f, completed_by: e.target.value }))}
                placeholder="Name of person completing checklist"
                required
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Job Ref</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={formData.job_ref}
                onChange={e => setFormData(f => ({ ...f, job_ref: e.target.value.toUpperCase() }))}
                placeholder="Which job is this checklist checking, e.g. K10251D"
                required
              />
            </div>
          </div>

          <hr className="my-6 dark:border-gray-700" />

          {/* Checklist Items by Section */}
          <h2 className="text-lg font-semibold mb-4">Mid-week Checklist Items</h2>
          {Object.entries(groupedBySection).map(([section, indices]) => (
            <div key={section} className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded">
                {section}
              </h3>
              <div className="space-y-3">
                {indices.map(idx => (
                  <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{responses[idx].item_description}</p>
                      </div>
                      <div className="flex gap-3 items-center">
                        <div className="flex gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`response-${idx}`}
                              value="yes"
                              checked={responses[idx].response === 'yes'}
                              onChange={() => handleResponseChange(idx, 'yes')}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Yes</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`response-${idx}`}
                              value="no"
                              checked={responses[idx].response === 'no'}
                              onChange={() => handleResponseChange(idx, 'no')}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">No</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`response-${idx}`}
                              value="na"
                              checked={responses[idx].response === 'na'}
                              onChange={() => handleResponseChange(idx, 'na')}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">N/A</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <input
                        type="text"
                        className={INPUT_CLS}
                        placeholder="Notes (optional)"
                        value={responses[idx].notes || ''}
                        onChange={e => handleNotesChange(idx, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Result Banner */}
          <div
            className={`p-3 rounded text-sm font-medium mb-6 ${
              currentResult === 'fail'
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            }`}
          >
            Result: {currentResult.toUpperCase()} {currentResult === 'fail' ? `(${responses.filter(r => r.response === 'no').length} items marked No)` : ''}
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-4">
            <Link to="/customs/etsf" className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Submit Checklist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
